// Floating status pill stack shown in the bottom-right corner of the
// AppLayout. One pill per active upload submit; concurrent submits stack
// vertically with the oldest at the bottom. Renders nothing when no
// submits are active.
//
// Per-pill visual states:
//   - uploading: spinning pill with the current file, project name, elapsed
//     time and the upload progress bar. Not dismissable.
//   - done: success pill linking to the project's Files tab. Dismissable.
//   - error: error pill with the message. Dismissable.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import type { UploadSubmitState } from "./useUploadSubmitContext";
import { useUploadSubmitContext } from "./useUploadSubmitContext";
import { formatDurationSeconds } from "../utils/formatDuration";
import styles from "./UploadStatusPill.module.css";

const formatElapsed = (ms: number): string => formatDurationSeconds(ms / 1000);

// Tick the displayed clock once a second so the elapsed hint stays fresh.
const useNowTick = (enabled: boolean): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [enabled]);
  return now;
};

export const UploadStatusPill = () => {
  const { submits } = useUploadSubmitContext();
  if (submits.length === 0) return null;
  return createPortal(
    <div className={styles.stack}>
      {submits.map((s) => (
        <SinglePill key={s.submitId} state={s} />
      ))}
    </div>,
    document.body,
  );
};

const SinglePill = ({ state }: { state: UploadSubmitState }) => {
  const { dismiss } = useUploadSubmitContext();
  const isBusy = state.stage === "uploading";
  const now = useNowTick(isBusy);
  const projectLabel = state.projectName ?? state.projectId;

  if (state.stage === "uploading") {
    const pct =
      state.uploadProgress.total > 0
        ? (state.uploadProgress.current / state.uploadProgress.total) * 100
        : 0;
    return (
      <div className={`${styles.pill} ${styles.busy}`} role="status">
        <div className={styles.row}>
          <span className={styles.spinner} aria-hidden />
          <strong>Uploading: {projectLabel}</strong>
        </div>
        <div className={styles.label}>{state.label}</div>
        <div className={styles.statusLine}>
          Started {formatElapsed(now - state.submitStartedAt)} ago
        </div>
        <div className={styles.progressTrack}>
          <div className={styles.progressFill} style={{ width: `${pct}%` }} />
        </div>
        <Link
          to={`/projects/${encodeURIComponent(state.projectId)}`}
          className={styles.hint}
        >
          Open project →
        </Link>
      </div>
    );
  }

  if (state.stage === "done") {
    return (
      <div className={`${styles.pill} ${styles.done}`} role="status">
        <div className={styles.row}>
          <strong>✓ Files uploaded: {projectLabel}</strong>
          <button
            type="button"
            onClick={() => dismiss(state.submitId)}
            aria-label="Dismiss"
            className={styles.dismiss}
          >
            ×
          </button>
        </div>
        <div className={styles.label}>{state.label}</div>
        <Link
          to={`/projects/${encodeURIComponent(state.projectId)}/files`}
          className={styles.cta}
          onClick={() => dismiss(state.submitId)}
        >
          Open files →
        </Link>
      </div>
    );
  }

  return (
    <div className={`${styles.pill} ${styles.error}`} role="alert">
      <div className={styles.row}>
        <strong>Upload failed: {projectLabel}</strong>
        <button
          type="button"
          onClick={() => dismiss(state.submitId)}
          aria-label="Dismiss"
          className={styles.dismiss}
        >
          ×
        </button>
      </div>
      <div className={styles.label}>{state.error?.message ?? "Unknown error"}</div>
    </div>
  );
};
